import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale, mq } from '@/theme';

export const useEventReviewPanelStyles = makeStyles({
  drawer: {
    width: '480px',
    maxWidth: '100vw',
  },
  headerTitle: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  title: {
    fontSize: fontScale.subtitle,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase200,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    ...shorthands.padding('8px', '16px', '8px', '24px'),
  },
  spaceSelector: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
  },
  spaceSelectorHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  spaceSelectorLabel: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  spaceDropdownRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    minWidth: 0,
  },
  spaceInputWrap: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
  },
  spaceInputCombobox: {
    width: '100%',
    '& input': {
      paddingRight: '112px',
    },
  },
  spaceDropdownTrigger: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    width: '100%',
    minWidth: 0,
  },
  spaceDropdownTriggerText: {
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  spaceBadgeInline: {
    flexShrink: 0,
    fontSize: fontScale.caption,
    whiteSpace: 'nowrap',
  },
  spaceBadgeInInput: {
    position: 'absolute',
    right: '34px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    fontSize: fontScale.caption,
    whiteSpace: 'nowrap',
  },
  spaceListbox: {
    maxHeight: '21.75rem !important' as unknown as string,
  },
  buildingListbox: {
    maxHeight: '21.75rem !important' as unknown as string,
  },
  spaceOptionGroupHeader: {
    ...shorthands.padding('6px', '12px'),
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  spaceOptionGroupLabel: {
    fontSize: fontScale.caption,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  spaceOptionContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  spaceOptionBooked: {
    opacity: 0.6,
  },
  spaceOptionTextBooked: {
    color: tokens.colorNeutralForeground4,
  },
  calendarLink: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    fontSize: fontScale.caption,
    color: tokens.colorBrandForegroundLink,
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    textDecorationLine: 'none',
    cursor: 'pointer',
    ...shorthands.padding('2px', '0'),
    width: 'fit-content',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  multiSpaceHeader: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  multiSpaceList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
    ...shorthands.padding(tokens.spacingVerticalXS, '0'),
  },
  multiSpaceItem: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase200,
  },
  showMoreLink: {
    fontSize: fontScale.caption,
    cursor: 'pointer',
    width: 'fit-content',
  },
  multiSpaceSummary: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground3,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('4px', '0'),
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  editButton: {
    minWidth: 'auto',
  },
  fieldGrid: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  editFormGrid: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
  },
  capacityWarningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap(tokens.spacingHorizontalS),
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderLeft: `${tokens.strokeWidthThick} solid ${tokens.colorPaletteRedBorder1}`,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  capacityWarningIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    color: tokens.colorPaletteRedForeground1,
    flexShrink: 0,
    marginTop: '2px',
  },
  fieldGridFullWidth: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    rowGap: tokens.spacingVerticalM,
  },
  personSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
  },
  personLabel: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  personChip: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('4px', '12px', '4px', '4px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('999px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    width: 'fit-content',
  },
  personName: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  personRole: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground3,
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    ...shorthands.margin('4px', '0'),
  },
  activitiesToggle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.padding('4px', '0'),
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    width: '100%',
  },
  activitiesList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
    ...shorthands.padding('0'),
  },
  activityDateGroup: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
    ...shorthands.margin('0', '0', tokens.spacingVerticalS, '0'),
  },
  activityDateHeader: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    ...shorthands.padding('4px', '0'),
  },
  activityTimelineItem: {
    display: 'flex',
    flexDirection: 'row',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding('4px', '0'),
    alignItems: 'flex-start',
  },
  activityCircle: {
    width: '16px',
    height: '16px',
    ...shorthands.borderRadius('50%'),
    ...shorthands.border('2px', 'solid', tokens.colorBrandForeground1),
    flexShrink: 0,
    marginTop: '2px',
  },
  activityContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
    flexGrow: 1,
    minWidth: 0,
  },
  activityTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  activityTime: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  activityDescription: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase200,
  },
  activityFieldChanges: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    ...shorthands.padding('4px', '0'),
  },
  activitySpaceChange: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  activitySpaceList: {
    listStyleType: 'disc',
    ...shorthands.margin('0'),
    paddingInlineStart: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  activitySpaceListItem: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase200,
  },
  activityItem: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  activityAction: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground1,
  },
  activityMeta: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    justifyContent: 'flex-start',
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  overflowButton: {
    marginLeft: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    ...shorthands.padding(tokens.spacingVerticalXXXL),
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingVerticalM),
    ...shorthands.padding(tokens.spacingVerticalXXXL),
    textAlign: 'center',
  },
  drawerOverride: {
    width: '610px',
    maxWidth: '100vw',
  },

  editActionsRow: {
    display: 'flex',
    ...shorthands.gap('8px'),
  },
  onSiteContactsGroup: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  onSiteContactHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
    alignItems: 'end',
    columnGap: tokens.spacingHorizontalS,
  },
  onSiteContactHeaderSingle: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  },
  onSiteContactHeaderLabel: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  requiredMark: {
    color: tokens.colorPaletteRedForeground1,
  },
  onSiteContactRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
  },
  onSiteContactRowSingle: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  },
  fieldLabelWithInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
  },
  infoIcon: {
    cursor: 'pointer',
    color: tokens.colorBrandForeground1,
  },
  spaceInfoIcon: {
    cursor: 'pointer',
    color: tokens.colorBrandForeground1,
    marginTop: '2px',
    marginLeft: '1px',
  },
  spaceInputRelative: {
    position: 'relative',
    flexGrow: 1,
    minWidth: 0,
  },
  selectedSpaceCapacity: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase200,
  },
  comboboxFull: {
    flexGrow: 1,
    minWidth: 0,
    width: '100%',
    '& input': {
      paddingRight: '70px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  comboboxNonInteractive: {
    pointerEvents: 'none',
  },
  badgeOverlay: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    fontSize: fontScale.caption,
    whiteSpace: 'nowrap',
  },
  timeRangeRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    minWidth: 0,
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalS,
    '& > *:not(span)': {
      flex: '1 1 7.5rem',
      minWidth: '7.5rem',
    },
    '& .fui-Combobox__input': {
      minWidth: 0,
    },
    [mq.mobile]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      rowGap: tokens.spacingVerticalS,
      '& > *:not(span)': {
        flex: '1 0 auto',
        minWidth: 0,
      },
    },
  },
  durationSeparator: {
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
    flexShrink: 0,
    [mq.mobile]: {
      display: 'none',
    },
  },
});
